import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { uploadProviderPhoto } from '@/services/providerService';
import { useProviderUpdate } from '@/hooks/useProviderUpdate';
import { Loader2, Upload, X, ImagePlus } from 'lucide-react';
import { toast } from 'sonner';

interface ManagePhotosModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  currentPhotos: string[];
  userId: string;
  onPhotosUpdate: (photos: string[]) => Promise<void>;
}

export const ManagePhotosModal = ({
  open,
  onOpenChange,
  currentPhotos,
  userId,
  onPhotosUpdate,
}: ManagePhotosModalProps) => {
  const [photos, setPhotos] = useState<string[]>(currentPhotos);
  const [uploadingIndex, setUploadingIndex] = useState<number | null>(null);
  const { deletePhoto, isDeleting } = useProviderUpdate();

  useEffect(() => {
    if (open) {
      setPhotos(currentPhotos);
    }
  }, [open, currentPhotos]);

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (photos.length >= 5) {
      toast.error('Maximum 5 photos allowed');
      return;
    }

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/jpg'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Only JPEG, PNG, and WebP images are allowed');
      return;
    }

    // Validate file size (50MB)
    if (file.size > 52428800) {
      toast.error(`File too large. Maximum size is 50MB`);
      return;
    }

    setUploadingIndex(photos.length);

    try {
      const photoUrl = await uploadProviderPhoto(file, userId);
      const newPhotos = [...photos, photoUrl];
      setPhotos(newPhotos);
      await onPhotosUpdate(newPhotos);
      toast.success('Photo uploaded successfully');
    } catch (error: any) {
      console.error('Upload error:', error);
      toast.error(error.message || 'Failed to upload photo');
    } finally {
      setUploadingIndex(null);
      event.target.value = '';
    }
  };

  const handleDeletePhoto = async (photoUrl: string, index: number) => {
    try {
      await deletePhoto(photoUrl);
      const newPhotos = photos.filter((_, i) => i !== index);
      setPhotos(newPhotos);
    } catch (error: any) {
      console.error('Delete error:', error);
      toast.error(error.message || 'Failed to delete photo');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ImagePlus className="w-5 h-5 text-primary" />
            Manage Business Photos ({photos.length}/5)
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Photo Grid */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {photos.map((photo, index) => (
              <div key={index} className="relative group aspect-square">
                <img
                  src={photo}
                  alt={`Business photo ${index + 1}`}
                  className="w-full h-full object-cover rounded-lg border"
                />
                <button
                  onClick={() => handleDeletePhoto(photo, index)}
                  disabled={isDeleting}
                  className="absolute top-2 right-2 p-1.5 bg-destructive text-destructive-foreground rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-destructive/90"
                >
                  {isDeleting ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <X className="w-4 h-4" />
                  )}
                </button>
              </div>
            ))}

            {/* Upload Button */}
            {photos.length < 5 && (
              <label
                htmlFor="photo-upload"
                className="relative aspect-square border-2 border-dashed rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-primary hover:bg-muted/50 transition-colors"
              >
                {uploadingIndex !== null ? (
                  <Loader2 className="w-8 h-8 text-muted-foreground animate-spin" />
                ) : (
                  <>
                    <Upload className="w-8 h-8 text-muted-foreground mb-2" />
                    <span className="text-sm text-muted-foreground text-center px-2">
                      Upload Photo
                    </span>
                  </>
                )}
                <input
                  id="photo-upload"
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  onChange={handleFileSelect}
                  disabled={uploadingIndex !== null}
                  className="hidden"
                />
              </label>
            )}
          </div>

          {/* Info */}
          <div className="text-sm text-muted-foreground space-y-1 p-4 bg-muted/20 rounded-lg">
            <p>• Maximum 5 photos allowed</p>
            <p>• Accepted formats: JPEG, PNG, WebP</p>
            <p>• Maximum file size: 50MB per photo</p>
            <p>• High-quality photos help attract more customers</p>
          </div>
        </div>

        <div className="flex justify-end">
          <Button onClick={() => onOpenChange(false)}>
            Done
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
